import { ConfigInfo, GetAssignedEditionsResponse, OrgExpandedAttributes, ServiceRegistry, SessionInfo, UserProducts } from '@infa-hawk/services';
import { IUser, userService } from '@infa-service/cdgc-app-facade';
import { GetAdditionalMetadataType, ReactAnalytics } from '@informatica/droplets-analytics';
import { FC, ReactNode, useCallback, useEffect } from 'react';

import { useIDQALoggerContext } from '../../../context/business/IDQAlogger/IDQALoggerContext';
import { useAppRegistrationContext } from '../../../context/business/Registration/AppRegistrationContext';
import { SessionStorage } from '../../../utils/storage-utils';
import { getAdditionalAccountMetadataCallback, GetLicenseInfoResponse } from './pendo-utils';

export const PENDO_ANALYTICS_STORAGE_KEY = 'PENDO_ANALYTICS_STORAGE_KEY';
export type TPendoSessionStorageDetails = {
	userInfo: IUser;
	sessionInfo: SessionInfo;
	licenseInfo: GetLicenseInfoResponse;
	assignedEditions: GetAssignedEditionsResponse;
	userProducts: UserProducts;
	configInfo: ConfigInfo;
	orgAttributes: Pick<OrgExpandedAttributes, 'city' | 'state' | 'country' | 'parentOrgId'>;
};
export enum PENOD_INIT_STATUS {
	SUCCESS,
	FAILED,
	LOADING,
}
const InitializePendoAnalytics: FC<{ children: ReactNode }> = ({ children }) => {
	const { userId, orgId } = useAppRegistrationContext();
	const { logger } = useIDQALoggerContext();
	const initializeAnalytics = useCallback(async (apiKey: string, analyticsProvider: string) => {
		try {
			let pendoAnalyticsDetails = SessionStorage.get<TPendoSessionStorageDetails>(PENDO_ANALYTICS_STORAGE_KEY);
			if (!pendoAnalyticsDetails) {
				const userInfo = await userService.getUserById(userId);
				const { identityService, sessionService, licenseService, cloudShellService } = ServiceRegistry.get();
				const sessionInfo = await sessionService.getSessionInfo();

				const [licenseInfo, assignedEditions, products, configInfo]: any = await Promise.all([
					licenseService.getLicenseInfoByOrgId({ orgId }),
					licenseService.getAssignedEditionsByOrgId({ orgId }),
					cloudShellService.getProducts(),
					identityService.getConfigInfo(),
				]);
				let orgAttributes = {
					city: '',
					state: '',
					country: '',
					parentOrgId: '',
				};
				try {
					orgAttributes = await identityService.getOrgExpandedAttributesByOrgId({ orgId });
				} catch (error) {
					logger.error('failed to get additional org attributes, initializing Pendo without additional org attributes', error);
				}

				pendoAnalyticsDetails = {
					userInfo,
					sessionInfo,
					licenseInfo,
					assignedEditions,
					userProducts: products,
					configInfo,
					orgAttributes,
				};
				SessionStorage.set<TPendoSessionStorageDetails>(PENDO_ANALYTICS_STORAGE_KEY, pendoAnalyticsDetails);
			}

			const {
				assignedEditions: { assignedEditions },
				userProducts: { userProducts },
				licenseInfo,
				sessionInfo,
				userInfo,
				orgAttributes,
				configInfo: { cloudProvider: hostingPlatform },
			} = pendoAnalyticsDetails;
			const cloudEditions = assignedEditions.map(({ name }: any) => name);
			const cloudServices = userProducts.map(({ products }: any) => products.map(({ defaultLabel }: any) => defaultLabel)).flat();

			const getAdditionalAccountMetadata: GetAdditionalMetadataType = await getAdditionalAccountMetadataCallback(licenseInfo);

			const analyticsConfig: any = {
				[analyticsProvider]: {
					enabled: true,
					trackingID: apiKey,
					getAdditionalAccountMetadata,
				},
			};

			const analyticsSessionInfo = {
				id: sessionInfo.id,
				orgId: sessionInfo.orgId,
				effectiveRoles: sessionInfo.effectiveRoles,
			};

			const analyticsData = {
				userProfile: {
					email: userInfo.email as string,
					jobTitle: '',
					timeZone: userInfo.timeZoneId as string,
					marketingOptIn: false,
				},
				orgAttributes: {
					name: sessionInfo.orgName,
					city: orgAttributes.city,
					state: orgAttributes.state,
					country: orgAttributes.country,
					parentOrgId: orgAttributes.parentOrgId,
					cloudServices,
					hostingPlatform,
					cloudEditions,
				},
			};
			ReactAnalytics.init(analyticsConfig, sessionInfo.id, analyticsSessionInfo, analyticsData);
		} catch (error) {
			logger.error('failed to initialize Pendo analytics', error);
		}
	}, []);

	useEffect(() => {
		const initializePendo = async () => {
			try {
				const { cloudShellService } = ServiceRegistry.get();
				const config = await cloudShellService.getConfig();
				const apiKey = config?.analytics?.pendo?.apiKey;

				if (apiKey) {
					await initializeAnalytics(apiKey, 'PENDO');
					return;
				}

				logger.error('Pendo API key is not found. Cannot initialize Pendo analytics.');
			} catch {
				logger.error('failed to initialize Pendo analytics.');
			}
		};
		initializePendo();
	}, []);

	return <> {children}</>;
};

export default InitializePendoAnalytics;
